// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import NoContentBackdrop from 'components/treactui-template/organisms/backdrop/NoContentBackdrop';
import useProjectVersionApi from 'hooks/useProjectModeVersionApi';
import { useParams } from 'react-router';
import VersionSelectionTable from './evaluate/VersionSelectionTable';
import ModelComparison from './evaluate/ModelComparison';
import { ProjectVersion, workingCopy } from 'model/ProjectMetaMessageExtensions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import sortBy from 'lodash/sortBy';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import useTrainingStatusApi from 'hooks/useTrainingStatusApi';
import BackdropLoading from 'components/treactui-template/organisms/backdrop/loading/BackdropLoading';
import useModelEvaluationTriggerApi from 'hooks/useModelEvaluationTriggerApi';
import EvaluateContext from './evaluate/EvaluateContext';
import ParamsWithId from 'model/ParamsWithId';
import { QCPContext } from '@tls/sw91_ui_templates';
import { setActiveMd5, setEvaluatedVersions } from 'store/reducers/projectEvaluationSlice';
import { ProjectStatus } from 'model/ProjectMetadata';
import useIsQC from 'hooks/useIsQC';
import { useQCEvaluateApi } from 'hooks/useQCEvaluateApi';
import AlgorithmsEvaluation from './evaluate/algorithms/AlgorithmsEvaluation';
import { Button, Colors, TabBar } from '@tls/treact-ui';
import { IconPlay } from '@tls/treact-ui/icons';

export default function Evaluate() {
  const evaluatedVersions = useAppSelector(state => state.evaluation.evaluatedVersions);
  const [selectedTab, setSelectedTab] = useState<'model' | 'algorithms'>('model');
  const [currentSelected, setCurrentSelected] = useState<ProjectVersion[]>(evaluatedVersions);
  const [sortedCurrentSelected, setSortedCurrentSelected] = useState<ProjectVersion[]>(evaluatedVersions);
  const { qcpProject } = useContext(QCPContext);

  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);
  const { changeTrainingStatus, loading: changingTraining } = useTrainingStatusApi(id ?? '');
  const isQC = useIsQC();
  const { trigger, loading: evaluationLoading, evaluationId } = useModelEvaluationTriggerApi(id ?? '');
  const { triggerQCEvaluation, evaluationResults, qcEvaluationProgress } = useQCEvaluateApi(id ?? '');
  const onClick = useCallback(
    (version: ProjectVersion) => async () => {
      const item = currentSelected.find(c => c === version);
      let newCurrentSelected: ProjectVersion[] = [];
      if (item) {
        newCurrentSelected = currentSelected.filter(t => t !== item);
      } else {
        newCurrentSelected = [...currentSelected, version];
      }
      setCurrentSelected(newCurrentSelected);
      setSortedCurrentSelected(sortBy(newCurrentSelected, ['major', 'minor', 'patch']));
    },
    [currentSelected]
  );

  const resetAll = useCallback(() => {
    setCurrentSelected([]);
    setSortedCurrentSelected([]);
  }, []);
  const dispatch = useAppDispatch();

  const evaluate = useCallback(async () => {
    dispatch(setActiveMd5(undefined));
    dispatch(setEvaluatedVersions([]));

    if (
      sortedCurrentSelected.includes(workingCopy) &&
      selectedProject?.projectStatus === ProjectStatus.TRAINING_ACTIVE
    ) {
      await changeTrainingStatus(ProjectStatus.TRAINING_STOPPED);
    }

    //allow browser to refresh size for selected options
    setTimeout(async () => {
      dispatch(setEvaluatedVersions(sortedCurrentSelected));
      trigger(sortedCurrentSelected);
    }, 10);

    if (!isQC || !qcpProject) {
      return;
    }

    for (const version of sortedCurrentSelected) {
      triggerQCEvaluation(qcpProject, { en: {} }, version);
    }
  }, [
    changeTrainingStatus,
    dispatch,
    selectedProject?.projectStatus,
    sortedCurrentSelected,
    trigger,
    qcpProject,
    isQC,
    triggerQCEvaluation,
  ]);

  const { t } = useTranslation();
  const { versions, loading, refresh } = useProjectVersionApi(id ?? '');

  const evaluateProviderValue = useMemo(() => ({ refresh }), [refresh]);
  return (
    <Container>
      <EvaluateContext.Provider value={evaluateProviderValue}>
        <VersionListContainer>
          <TableContainer>
            <VersionSelectionTable
              loading={loading}
              versions={versions}
              selected={sortedCurrentSelected}
              resetAll={resetAll}
              onClick={onClick}
              disableUnselected={sortedCurrentSelected.length > 2}
            />
          </TableContainer>
          <ButtonContainer>
            <Button
              icon={<IconPlay />}
              label={t('project.evaluation.evaluate', { selected: currentSelected.length, total: 3 })}
              variant={evaluatedVersions.length > 0 ? 'secondary' : 'primary'}
              onClick={evaluate}
              data-testid='evaluation-start-button'
              disabled={
                currentSelected.length < 1 ||
                currentSelected.length > 3 ||
                sortedCurrentSelected.map(s => s).join(',') === evaluatedVersions.map(s => s).join(',')
              }
            />
          </ButtonContainer>
        </VersionListContainer>
        {isQC && (
          <TabContainer>
            <TabBar>
              <TabBar.ContentTabInformation
                label='Model'
                selected={selectedTab === 'model'}
                onClick={() => setSelectedTab('model')}
                data-testid='evaluation-model-tab'
              />
              <TabBar.ContentTabInformation
                label='Algorithms'
                selected={selectedTab === 'algorithms'}
                onClick={() => setSelectedTab('algorithms')}
                data-testid='evaluation-algorithms-tab'
              />
            </TabBar>
          </TabContainer>
        )}
        <Evaluation>
          {selectedTab === 'model' ? (
            <ModelComparison selected={evaluatedVersions} evaluationId={evaluationId} />
          ) : (
            <AlgorithmsEvaluation
              versions={evaluatedVersions}
              evaluationResults={evaluationResults}
              progress={qcEvaluationProgress}
            />
          )}
          <NoContentBackdrop
            show={versions?.length < 1 && !loading}
            title={t('project.evaluation.versions.noData.title')}
            details={t('project.evaluation.versions.noData.details')}
          />
          <NoContentBackdrop
            show={
              versions?.length > 0 &&
              (evaluatedVersions.length < 1 || (evaluatedVersions.length < 1 && currentSelected.length < 1))
            }
            title={t('project.evaluation.prompt.title')}
            details={t('project.evaluation.prompt.details', { selected: currentSelected.length, total: 3 })}
          />
          <BackdropLoading loading={changingTraining} label={t('project.label.training.stopping')} />
          <BackdropLoading loading={evaluationLoading} label={t('project.label.training.evaluating')} />
        </Evaluation>
      </EvaluateContext.Provider>
    </Container>
  );
}

const TableContainer = styled.div`
  height: calc(100% - 3.5rem);
  overflow-y: hidden;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0.5rem 0rem;
`;

const Evaluation = styled.div`
  position: relative;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  position: relative;
`;

const VersionListContainer = styled.div`
  height: inherit;
  min-width: 12rem;
  border-right: 1px solid ${Colors.trgrey2.hex};
`;

const TabContainer = styled.div`
  width: 140px;
  flex-shrink: 0;
  height: 100%;
`;
